import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Terminal, X, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "@/lib/apiClient";

const APP_LINK_RE = /(\/handbook#[a-z-]+|\/truth\/[a-z0-9-]+)/g;
const APP_LINK_TEST_RE = /^(\/handbook#[a-z-]+|\/truth\/[a-z0-9-]+)$/;

function renderContent(text) {
  const parts = text.split(APP_LINK_RE);
  return parts.map((part, i) =>
    APP_LINK_TEST_RE.test(part) ? (
      <Link key={i} to={part} className="underline text-openai-cyan hover:text-white" data-testid="buddy-handbook-link">
        {part}
      </Link>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

export const BuddyWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loadedHistory, setLoadedHistory] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && !loadedHistory) {
      fetch(`${API}/buddy/history`, { credentials: "include" })
        .then((r) => r.json())
        .then((data) => setMessages(data.messages || []))
        .catch(() => {})
        .finally(() => setLoadedHistory(true));
    }
  }, [open, loadedHistory]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streaming]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || streaming || !loadedHistory) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    const appendDelta = (content) => {
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (!last || last.role !== "assistant") {
          next.push({ role: "assistant", content });
        } else {
          next[next.length - 1] = { role: "assistant", content: last.content + content };
        }
        return next;
      });
    };

    const replaceLastAssistant = (content) => {
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (!last || last.role !== "assistant") {
          next.push({ role: "assistant", content });
        } else {
          next[next.length - 1] = { role: "assistant", content };
        }
        return next;
      });
    };

    try {
      const res = await fetch(`${API}/buddy/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6);
          if (raw === "[DONE]") continue;
          const parsed = JSON.parse(raw);
          if (parsed.delta) {
            appendDelta(parsed.delta);
          } else if (parsed.error) {
            replaceLastAssistant(parsed.error);
          }
        }
      }
    } catch {
      replaceLastAssistant("INTELLIGENCE LOG: Connection to Officer lost. Retry transmission.");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-40" data-testid="buddy-widget">
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="flex h-14 w-14 items-center justify-center rounded-full border border-truth-green/40 bg-obsidian text-truth-green shadow-[0_0_24px_rgba(16,185,129,0.35)] transition-transform hover:scale-105"
          data-testid="buddy-widget-open-btn"
        >
          <Terminal className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div className="flex h-[520px] w-[360px] flex-col overflow-hidden rounded-lg border border-truth-green/30 bg-black font-mono shadow-[0_0_40px_rgba(16,185,129,0.2)]" data-testid="buddy-widget-panel">
          <div className="flex items-center justify-between border-b border-truth-green/30 bg-obsidian px-4 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-truth-green">BUDDY :: Sovereign Intelligence Officer</p>
            <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white" data-testid="buddy-widget-close-btn">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-3 text-xs" data-testid="buddy-widget-messages">
            {messages.length === 0 && (
              <p className="text-truth-green/70">
                &gt; INTELLIGENCE LOG: Officer online. Query the Directorate on GEO, Visibility Scores, Conflicts, or Truth Statement execution.
              </p>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "text-openai-cyan" : "text-truth-green"} data-testid={`buddy-message-${i}`}>
                <span className="text-slate-500">{m.role === "user" ? "you@sovereign:~$ " : "> "}</span>
                <span className="whitespace-pre-wrap">{renderContent(m.content)}</span>
                {streaming && i === messages.length - 1 && m.role === "assistant" && (
                  <span className="terminal-cursor text-truth-green">&#9608;</span>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={send} className="flex items-center gap-2 border-t border-truth-green/30 bg-obsidian p-2">
            <span className="text-xs text-truth-green">$</span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={loadedHistory ? "Transmit query..." : "Loading intelligence log..."}
              disabled={streaming || !loadedHistory}
              className="flex-1 bg-transparent text-xs text-white placeholder:text-slate-600 focus:outline-none"
              data-testid="buddy-widget-input"
            />
            <Button type="submit" size="sm" disabled={streaming || !loadedHistory || !input.trim()} className="h-7 gap-1 bg-truth-green px-2 text-obsidian hover:bg-truth-green/90" data-testid="buddy-widget-send-btn">
              <Send className="h-3 w-3" />
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};
